import axios, { AxiosError, AxiosResponse, CanceledError } from 'axios';
import { parseAxiosError } from '../common/constant';
import { JIRA_MANAGEMENT_BASEURL, JIRA_USERNAME, JIRA_TOKEN, JIRA_EPIC_ISSUE_ID, BASEURL } from './const';
import { dispatchSessionEvent } from '../components/event/dispatchSessionEvent';
import { encrypt } from '../utils/auth';
import moment from 'moment';
import { ICommentField, IJiraIssue, IJiraUserDetails, IResponse, IUpdateJiraIssueStatusParam } from "../common/interface";

declare module 'axios' {
    interface InternalAxiosRequestConfig {
        __retryCount?: number; // Optional property to track retries
    }
}

const MAX_RETRIES = 2; // Number of retry attempts
const RETRY_DELAY_BASE = 1000;
const AXIOS_NETWORK_ERROR_CODE = "ERR_NETWORK"

// Utility function to determine if an error might be a CORS-related error
const isCorsError = (error: AxiosError): boolean => {
    if (!error.response && error?.code?.toLocaleUpperCase() === AXIOS_NETWORK_ERROR_CODE) {
        return true;
    }
    return false;
};

// Utility function to determine if an error might be a retryable gateway error
const isRetryableError = (error: AxiosError): boolean => {
    if (error.response) {
        const status = error.response.status;
        return (
            status === 502 || status === 503 || status === 504
        ); // Common gateway error status codes
    }
    return isCorsError(error);
};


export const jiraApi = axios.create()
const jiraUserApi = axios.create()
/* HTTP Request Interceptor to add headers on all API calls */
jiraApi.interceptors.request.use(
    config => {

        const jiraAccessToken = localStorage.getItem('jiraAccessToken') || ""
        const access_token = jiraAccessToken.replace(/"/g, "")
        if (!access_token) {
            return Promise.reject("User don't have jira access");
        }
        config.headers['authorization'] = `Bearer ${access_token}`
        return config;
    },
    error => {
        Promise.reject(error);
    }
)

// Implement response interceptor with retry mechanism
jiraApi.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    async (error: AxiosError) => {
        // Check if it's a retryable error
        const isRetryable = isRetryableError(error);
        const retryCount = error?.config?.__retryCount || 0;

        if (error?.config && isRetryable && retryCount < MAX_RETRIES) {
            // Increment the retry count
            error.config.__retryCount = retryCount + 1;
            const delay = RETRY_DELAY_BASE // * Math.pow(2, retryCount); // Exponential backoff
            await new Promise((resolve) => setTimeout(resolve, delay));

            // Retry the request
            return jiraApi(error.config);
        }

        return Promise.reject(error);
    }
);

export const getNewAccessAndRefreshToken = async () => {
    try {
        console.log("[TESTING] JIRA Calling refresh token at" + moment(new Date()).format('YYYY-MM-DD HH:mm:ss'));
        const jiraRefreshToken = localStorage.getItem('jiraRefreshToken') || ""
        if (!jiraRefreshToken) {
            throw new Error("jira Refresh Token not found")
        }
        const refreshToken = jiraRefreshToken.replace(/"/g, "")
        const response = await jiraApi.post(`${JIRA_MANAGEMENT_BASEURL}/jira/refresh-token`, { refresh_token: refreshToken })
        const { refresh_token, access_token } = response?.data?.data || {}
        localStorage.setItem('jiraAccessToken', JSON.stringify(access_token));
        localStorage.setItem('jiraRefreshToken', JSON.stringify(refresh_token));
        return response?.data;
    } catch (error) {
        console.error('[FAILED] to refresh Jira token:', error);
        dispatchSessionEvent('sessionExpired', { value: true });
        return parseAxiosError(error as AxiosError);
    }
}

export const getUserJiraProject = async (accountId: string) => {
    try {
        const response = await jiraApi.get(`${JIRA_MANAGEMENT_BASEURL}/jira/projects`, {
            params: {
                assignee: accountId
            }
        })
        return response?.data;
    } catch (error: any) {
        return parseAxiosError(error as AxiosError);
    }
}

export const getJiraTrackedTime = async (accountId: string) => {
    try {
        const response = await jiraApi.get(`${JIRA_MANAGEMENT_BASEURL}/jira/log-time`, {
            headers: {
                username: encrypt(JIRA_USERNAME),
                token: encrypt(JIRA_TOKEN)
            },
            params: {
                assignee: accountId
            }
        });
        return response?.data;
    } catch (error: any) {
        return parseAxiosError(error as AxiosError);
    }
}

/** get jira task based on projectkey, if project key not passed get all task for the user accountId */
interface getJiraProjectTaskParam {
    accountId: string
    projectKey?: string,
    isListCompleted?: boolean,
    filterIssueTypeId?: string,
    searchIssueText?: string,
    signal?: AbortSignal
    reporter?: string,
    startDate?: string,
    endDate?: string,
    showCompleted?: boolean,
    showPending?: boolean,
}
export const getJiraProjectTask = async ({ accountId, projectKey, isListCompleted, filterIssueTypeId, searchIssueText, reporter, showCompleted, showPending, signal, startDate, endDate }: getJiraProjectTaskParam) => {
    try {
        const response = await jiraApi.get(`${JIRA_MANAGEMENT_BASEURL}/jira/tasks`, {
            signal,
            params: {
                key: `${projectKey || ''}`,
                assignee: accountId,
                type: filterIssueTypeId || '',
                isCompleted: isListCompleted || '',
                text: searchIssueText || '',
                reporter,
                showCompleted,
                showPending,
                startDate,
                endDate,
            }
        })
        return response?.data;
    } catch (error: any) {
        return parseAxiosError(error as AxiosError);
    }
}

interface getJiraEpicsParams {
    projectKey: string,
    searchText?: string
}

export const getJiraEpics = async ({ projectKey, searchText }: getJiraEpicsParams, signal?: AbortSignal) => {

    try {
        const response = await jiraApi.get(`${JIRA_MANAGEMENT_BASEURL}/jira/tasks`, {
            signal: signal,
            params: {
                type: JIRA_EPIC_ISSUE_ID,
                text: searchText,
                maxResult: 10000,
                key: `${projectKey || ''}`
            }
        })
        return response?.data;
    } catch (error) {
        return parseAxiosError(error as AxiosError);
    }
}

export const getJiraResponsibleTeam = async () => {
    try {
        const response = await jiraApi.get(`${JIRA_MANAGEMENT_BASEURL}/jira/responsible-team`, {
            headers: {
                username: encrypt(JIRA_USERNAME),
                token: encrypt(JIRA_TOKEN)
            },
        });
        return response?.data;
    } catch (error: any) {
        return parseAxiosError(error as AxiosError);
    }
}

export const updateJiraIssueStatus = async (data: IUpdateJiraIssueStatusParam) => {
    try {
        const response = await jiraApi.post(`${JIRA_MANAGEMENT_BASEURL}/jira/issue-status`, data)
        return response?.data;
    } catch (error: any) {
        return parseAxiosError(error as AxiosError);
    }
}

interface addJiraCommentForTaskParam {
    issueKey: string,
    body: string
}
export const addJiraCommentForTask = async (data: addJiraCommentForTaskParam) => {
    try {
        const response = await jiraApi.post(`${JIRA_MANAGEMENT_BASEURL}/jira/comments`, data)
        return response?.data;
    } catch (error: any) {
        return parseAxiosError(error as AxiosError);
    }
}

export const getJiraProjectIssueTypes = async (projectKey: string) => {
    try {
        const response = await jiraApi.get(`${JIRA_MANAGEMENT_BASEURL}/jira/issue-type`, {
            params: {
                projectId: `${projectKey || ''}`,
            }
        })
        return response?.data;
    } catch (error: any) {
        return parseAxiosError(error as AxiosError);
    }
}

export const getJiraProjectComponents = async (projectKey: string) => {
    try {
        const response = await jiraApi.get(`${JIRA_MANAGEMENT_BASEURL}/jira/components`, {
            params: {
                projectKey: `${projectKey || ''}`,
            }
        })
        return response?.data;
    } catch (error: any) {
        return parseAxiosError(error as AxiosError);
    }
}

export const createJiraTask = async (data: any) => {
    try {
        const response = await jiraApi.post(`${JIRA_MANAGEMENT_BASEURL}/jira/tasks`, data)
        return response?.data;
    } catch (error: any) {
        return parseAxiosError(error as AxiosError);
    }
}

export const loggingJiraTaskTrackedTime = async (data: any) => {
    try {
        const response = await jiraApi.post(`${JIRA_MANAGEMENT_BASEURL}/jira/work-log`, data)
        return response?.data;

    } catch (error) {
        return parseAxiosError(error as AxiosError);
    }
}

interface IGetJiraUserParam {
  size?: number;
  text?: string;
  signal?: AbortSignal;
}
export const getJiraUser = async ({
  text,
  size,
  signal,
}: IGetJiraUserParam): Promise<IResponse<IJiraUserDetails[]>> => {
  try {
    const url = `${BASEURL}/jira/users`;
    const response = await jiraApi.get(url, {
      signal,
      params: {
        text: text || "",
        maxResults: size,
      },
    });
    return response?.data;
  } catch (error: any) {
    return parseAxiosError(error as AxiosError);
  }
};

interface IUpdateJiraTaskPayload {
  id: string; // Jira issue/task Id
  fields: Record<string, any>;
}
export const updateTaskDetails = async (
  payload: IUpdateJiraTaskPayload
): Promise<IResponse<IJiraIssue>> => {
  try {
    const response = await jiraApi({
      method: "PUT",
      url: `${BASEURL}/jira/task`,
      data: payload,
    });
    return response?.data;
  } catch (error: any) {
    return parseAxiosError(error as AxiosError);
  }
};

export const addMeetingTaskInJira = async (data: any) => {
    try {
        const response = await jiraApi.post(`${JIRA_MANAGEMENT_BASEURL}/jira/create-meet`, data);
        return response?.data;

    } catch (error) {
        return parseAxiosError(error as AxiosError);
    }
}

interface updateMeetingTaskInJiraParam {
    issueKey: string,
    formData: FormData
}
export const updateMeetingTaskInJira = async ({ issueKey, formData }: updateMeetingTaskInJiraParam) => {
    try {
        const response = await jiraApi.post(`${JIRA_MANAGEMENT_BASEURL}/jira/task/${issueKey}/video-upload`,
            formData,
            {
                headers: { 'Content-Type': 'multipart/form-data', }
            }
        )
        return response?.data;

    } catch (error) {
        return parseAxiosError(error as AxiosError);
    }
}

export const createJiraSubTaskForAdditionalInfo = async (data: any) => {
    try {
        const response = await jiraApi.post(`${JIRA_MANAGEMENT_BASEURL}/jira/actions`, data, {
            headers: {
                username: encrypt(JIRA_USERNAME),
                token: encrypt(JIRA_TOKEN)
            },
        });
        return response?.data;
    } catch (error: any) {
        return parseAxiosError(error as AxiosError);
    }
}

export const getIssueActions = async (id: number) => {
    try {
        const response = await jiraApi.get(`${JIRA_MANAGEMENT_BASEURL}/jira/actions/${id}`, {
            headers: {
                username: encrypt(JIRA_USERNAME),
                token: encrypt(JIRA_TOKEN)
            },
        });
        return response?.data;
    } catch (error: any) {
        return parseAxiosError(error as AxiosError);
    }
}

export const searchUser = async (text?: string) => {
    try {
        let url = `${JIRA_MANAGEMENT_BASEURL}/jira/user?email=${text}`;
        const response = await jiraApi.get(url);
        return response?.data;
    } catch (error: any) {
        return parseAxiosError(error as AxiosError);
    }
}

export const loggingJiraTaskTrackedTimeInDB = async (data: any) => {
    try {
        const response = await jiraApi.post(`${JIRA_MANAGEMENT_BASEURL}/jira/add-work-log`, data)
        return response?.data;
    } catch (error) {
        return parseAxiosError(error as AxiosError);
    }
}

export const syncTimeWithJira = async (accountId: string) => {
    try {
        const response = await jiraApi.get(`${JIRA_MANAGEMENT_BASEURL}/jira/sync-log`, {
            params: {
                accountId,
            }
        });
        return response?.data;
    } catch (error) {
        return parseAxiosError(error as AxiosError);
    }
}

export const getAttachmentsAndComments = async (issueKey: string) => {
    try {
        const response = await jiraApi.get(`${JIRA_MANAGEMENT_BASEURL}/jira/attachments-and-comments/${issueKey}`);
        if (response && response?.status === 200) {
            return response?.data?.data;
        }
        return {};
    } catch (error) {
        return parseAxiosError(error as AxiosError);
    }
}

export const getJiraTaskDetails = async (
  issueKey: string,
  signal: AbortSignal,
  fields: string = "description,comment,attachment,assignee",
  expand: string = "renderedFields"
) => {
  try {
    const response = await jiraApi.get(`${BASEURL}/jira/task/${issueKey}`, {
      params: { fields, expand },
      signal,
    });
    if (response && response?.status === 200) {
      return response?.data;
    }
    return {};
  } catch (error) {
    if (error instanceof CanceledError) {
      console.log("Request for task details aborted.");
      return {};
    }
    return parseAxiosError(error as AxiosError);
  }
};

export const getJiraTaskCommentDetails = async (
  jiraTaskSelf: string,
  signal: AbortSignal
): Promise<ICommentField | {}> => {
  try {
    const response: AxiosResponse<ICommentField> = await jiraApi.get(
      `${jiraTaskSelf}/comment`,
      { signal }
    );
    if (response?.status === 200) {
      return response?.data;
    }
    return {};
  } catch (error) {
    if (error instanceof CanceledError) {
      console.log("Request for comments aborted.");
      return {};
    }
    return parseAxiosError(error as AxiosError);
  }
};

export const getJiraAttachment = async (srcUrl: string) => {
  try {
    const response = await jiraApi.get(srcUrl, { responseType: "blob" });
    if (response && response?.status === 200) {
      return response?.data;
    }
    return {};
  } catch (error) {
    return parseAxiosError(error as AxiosError);
  }
};

export const getAttachment = async (url: string) => {
    try {
        const response = await jiraApi.get(url, {
            headers: {
                'X-Altassian-Token': 'no-check'
            }
        });

        if (response && response?.status === 200) {
            return response?.data;
        }
        return {};
    } catch (error) {
        return parseAxiosError(error as AxiosError);
    }
}

export const getTransitions = async (issueKey: string) => {
    try {
        const response = await jiraApi.get(`${JIRA_MANAGEMENT_BASEURL}/jira/transitions?issueKey=${issueKey}`);
        if (response && response?.status === 200) {
            return response?.data?.data;
        }
        return {};
    } catch (error) {
        return parseAxiosError(error as AxiosError);
    }
}

export const getLogSummary = async (accountId: string, timeZone: string) => {
    try {
        const response = await jiraApi.get(`${JIRA_MANAGEMENT_BASEURL}/jira/log-summary`, {
            params: {
                accountId,
                timeZone
            }
        });
        return response?.data;
    } catch (error) {
        return parseAxiosError(error as AxiosError);
    }
}

export const validateJiraUser = async (email: string, token: string) => {
    try {
        const response = await jiraUserApi.get(`${JIRA_MANAGEMENT_BASEURL}/jira/myself`, {
            headers: {
                'x-jira-token': 'Basic ' + btoa(`${email}:${token}`)
            },
        });
        return response?.data;
    } catch (error: any) {
        return parseAxiosError(error as AxiosError);
    }
}

export const getJiraRedirectUrl = async (): Promise<any> => {
    try {
        const response = await axios.get(`${BASEURL}/jira/redirect-url`);
        return response?.data;
    } catch (error: any) {
        return parseAxiosError(error as AxiosError);
    }
}

export const getJiraToken = async (code: string): Promise<any> => {
    try {
        const response = await axios.get(`${BASEURL}/jira/getJiraToken?code=${code}`);
        return response?.data;
    } catch (error: any) {
        return parseAxiosError(error as AxiosError);
    }
}

export const getJiraAccessToken = async (token: string): Promise<any> => {
    try {
        const refreshToken = token.replace(/"/g, "")
        const response = await axios.post(`${JIRA_MANAGEMENT_BASEURL}/jira/refresh-token`, { refresh_token: refreshToken })
        return response?.data;
    } catch (error: any) {
        return parseAxiosError(error as AxiosError);
    }
}

export const validateUser = async (token: string): Promise<any> => {
    try {
        const response = await axios.get(`${BASEURL}/jira/myself`, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response?.data;
    } catch (error: any) {
        return parseAxiosError(error as AxiosError);
    }
}

export const updateConfluenceLinkField = async (data: any) => {
    try {
        const response = await jiraApi.put(`${JIRA_MANAGEMENT_BASEURL}/jira/update-confluence-link`, data)
        return response?.data;
    } catch (error: any) {
        return parseAxiosError(error as AxiosError);
    }
}

export const addComponent = async (data: { projectKey: string, name: string, leadAccountId: string, projectName: string }) => {
    try{
        const response = await jiraApi.post(`${JIRA_MANAGEMENT_BASEURL}/jira/add-component`, data)
        return response?.data;
    }catch (error: any){
        return parseAxiosError(error as AxiosError)
    }
}

export const getPermissions = async (projectName: string, permission: string) => {
    try{
        const response = await jiraApi.get(`${JIRA_MANAGEMENT_BASEURL}/jira/get-permission?projectName=${projectName}&permission=${permission}`)
        return response?.data;
    }catch (error: any){
        return parseAxiosError(error as AxiosError)
    }
}
